/**
 * Offline Sync Queue
 * Stores failed API calls in localStorage and retries when back online.
 */

const QUEUE_KEY = 'offline_sync_queue';

export interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  createdAt: string;
  type: string; // 'jaap_sync' | 'sadhak_entry' | 'user_profile'
}

// Get all queued requests
export const getQueue = (): QueuedRequest[] => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
};

// Add a request to the queue
export const enqueue = (req: Omit<QueuedRequest, 'id' | 'createdAt'>): void => {
  const queue = getQueue();
  queue.push({
    ...req,
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    createdAt: new Date().toISOString(),
  });
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  window.dispatchEvent(new CustomEvent('offlineQueueChange', { detail: { count: queue.length } }));
};

// Remove a request from queue
const dequeue = (id: string): void => {
  const queue = getQueue().filter(q => q.id !== id);
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  window.dispatchEvent(new CustomEvent('offlineQueueChange', { detail: { count: queue.length } }));
};

// Process all queued requests (call when back online)
export const processQueue = async (): Promise<{ success: number; failed: number }> => {
  const queue = getQueue();
  if (queue.length === 0) return { success: 0, failed: 0 };

  window.dispatchEvent(new CustomEvent('offlineSyncStart', { detail: { total: queue.length } }));

  let success = 0;
  let failed = 0;

  for (const req of queue) {
    try {
      const res = await fetch(req.url, {
        method: req.method,
        headers: req.headers,
        body: req.body,
      });
      if (res.ok || res.status === 401) {
        // 401 = token expired, remove anyway (can't retry)
        dequeue(req.id);
        success++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }

  window.dispatchEvent(new CustomEvent('offlineSyncComplete', { detail: { success, failed } }));
  return { success, failed };
};

// Helper: Try fetch, if offline → enqueue
export const fetchWithOfflineQueue = async (
  url: string,
  options: RequestInit & { queueType?: string } = {}
): Promise<Response | null> => {
  const { queueType = 'api_call', ...fetchOptions } = options;

  try {
    const res = await fetch(url, fetchOptions);
    return res;
  } catch (err) {
    // Network error = probably offline
    if (!navigator.onLine) {
      enqueue({
        url,
        method: fetchOptions.method || 'GET',
        headers: (fetchOptions.headers as Record<string, string>) || {},
        body: (fetchOptions.body as string) || '',
        type: queueType,
      });
    }
    return null;
  }
};

// Get pending count
export const getPendingCount = (): number => getQueue().length;

// Auto-process queue when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    setTimeout(() => processQueue(), 1500);
  });
}
